import { CSRF_HEADER } from "@lms/backend/lib/csrf-constants";
import { apiUrl } from "@/services/http/api-url";
import { fetchCsrfToken, invalidateCsrfCache } from "@/services/http/csrf-token";
import { refreshPlatformSessionOnce } from "@/services/http/platform-session-refresh";
import { usePlatformAuthStore } from "@/stores/platformAuthStore";

/** Platform routes whose 401 means "wrong credentials or link", never "session expired". */
const PLATFORM_CREDENTIAL_PATHS = [
  "/platform/auth/login",
  "/platform/auth/verify",
  "/platform/auth/resend",
  "/platform/auth/refresh",
  "/platform/auth/logout",
  "/platform/auth/set-password",
  "/platform/auth/forgot-password",
];

function isCredentialPath(url: string): boolean {
  const path = url.replace(/^https?:\/\/[^/]+/i, "").split(/[?#]/)[0] ?? "";
  return PLATFORM_CREDENTIAL_PATHS.some((p) => path.endsWith(p));
}

async function isCsrfRefusal(res: Response): Promise<boolean> {
  try {
    const body = (await res.clone().json()) as { error?: { code?: unknown } };
    return body?.error?.code === "CSRF_INVALID";
  } catch {
    return false;
  }
}

/**
 * MT-1 AUTH-009 (FR-6.6): the platform console's fetch. It sends only the platform access token
 * (never the school one), CSRF on EVERY mutation (no exemption, `/platform/auth/*` included),
 * one retry on a CSRF refusal, and answers a 401 with one platform refresh and one retry.
 */
export async function platformFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = apiUrl(path);
  const method = (init.method ?? "GET").toUpperCase();
  const isMutating = ["POST", "PUT", "PATCH", "DELETE"].includes(method);

  async function send(freshCsrf: boolean): Promise<Response> {
    const headers = new Headers(init.headers);
    const token = usePlatformAuthStore.getState().accessToken;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    else headers.delete("Authorization");
    if (isMutating) headers.set(CSRF_HEADER, await fetchCsrfToken(freshCsrf));
    if (typeof init.body === "string" && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return fetch(url, { ...init, credentials: "include", headers });
  }

  async function sendWithCsrfRetry(): Promise<Response> {
    const res = await send(false);
    if (isMutating && res.status === 403 && (await isCsrfRefusal(res))) {
      invalidateCsrfCache();
      return send(true);
    }
    return res;
  }

  const res = await sendWithCsrfRetry();
  if (res.status !== 401 || isCredentialPath(url)) return res;
  if ((await refreshPlatformSessionOnce()) !== "refreshed") return res;
  return sendWithCsrfRetry();
}
